'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, UserPlus, ChevronDown } from 'lucide-react';
import type { AuthState } from './AuthPage';
import { useLanguage } from '@/contexts/LanguageContext';
import { checkRateLimit, sanitizeInput } from '@/lib/security';

interface RegisterFormProps {
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  agreeTerms: boolean;
}

const countryCodes = [
  { code: '+41', flag: '🇨🇭', name: 'CH' },
  { code: '+44', flag: '🇬🇧', name: 'GB' },
  { code: '+1', flag: '🇺🇸', name: 'US' },
  { code: '+49', flag: '🇩🇪', name: 'DE' },
  { code: '+33', flag: '🇫🇷', name: 'FR' },
  { code: '+234', flag: '🇳🇬', name: 'NG' },
  { code: '+91', flag: '🇮🇳', name: 'IN' },
  { code: '+27', flag: '🇿🇦', name: 'ZA' },
  { code: '+254', flag: '🇰🇪', name: 'KE' },
  { code: '+971', flag: '🇦🇪', name: 'AE' },
  { code: '+65', flag: '🇸🇬', name: 'SG' },
  { code: '+61', flag: '🇦🇺', name: 'AU' },
];

export default function RegisterForm({ authState, setAuthState }: RegisterFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterData>();

  const onSubmit = async (data: RegisterData) => {
    // Rate limit: max 3 registration attempts per 10 minutes
    const rl = checkRateLimit('register', 3, 10 * 60 * 1000, 30 * 60 * 1000);
    if (!rl.allowed) {
      const mins = rl.lockedUntilMs ? Math.ceil((rl.lockedUntilMs - Date.now()) / 60000) : 30;
      setRateLimitError(`Too many registration attempts. Please try again in ${mins} minute(s).`);
      return;
    }
    setRateLimitError('');
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setAuthState((prev) => ({
      ...prev,
      phone: `${selectedCountry.code}${sanitizeInput(data.phone)}`,
      view: 'otp',
    }));
    setIsLoading(false);
  };

  return (
    <div className="fade-in">
      <button
        onClick={() => setAuthState((prev) => ({ ...prev, view: 'login' }))}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft size={15} />
        {t.auth.backToSignIn}
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1.5">{t.auth.openYourAccount}</h2>
        <p className="text-sm text-muted-foreground">
          {t.auth.openAccountSubtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t.auth.firstName}
            </label>
            <input
              type="text"
              placeholder="Amara"
              className={`input-field ${errors.firstName ? 'error' : ''}`}
              {...register('firstName', { required: t.common.required })}
            />
            {errors.firstName && (
              <p className="text-xs text-negative mt-1">{errors.firstName.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t.auth.lastName}
            </label>
            <input
              type="text"
              placeholder="Osei"
              className={`input-field ${errors.lastName ? 'error' : ''}`}
              {...register('lastName', { required: t.common.required })}
            />
            {errors.lastName && (
              <p className="text-xs text-negative mt-1">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t.auth.emailAddress}
          </label>
          <input
            type="email"
            placeholder="amara.osei@email.com"
            className={`input-field ${errors.email ? 'error' : ''}`}
            {...register('email', {
              required: t.auth.emailRequired,
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t.auth.emailInvalid },
            })}
          />
          {errors.email && (
            <p className="text-xs text-negative mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t.auth.mobileNumber}
          </label>
          <div className="flex gap-2">
            {/* Country code selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCountryPicker(!showCountryPicker)}
                className="h-full flex items-center gap-1.5 px-3 bg-input border border-border rounded-lg text-sm font-medium text-foreground hover:border-primary/40 transition-colors whitespace-nowrap"
              >
                <span>{selectedCountry.flag}</span>
                <span>{selectedCountry.code}</span>
                <ChevronDown size={13} className="text-muted-foreground" />
              </button>
              {showCountryPicker && (
                <div className="absolute top-full left-0 mt-1 w-44 card-elevated z-20 py-1 slide-up max-h-56 overflow-y-auto">
                  {countryCodes.map((c) => (
                    <button
                      key={`reg-cc-${c.code}`}
                      type="button"
                      onClick={() => { setSelectedCountry(c); setShowCountryPicker(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <span>{c.flag}</span>
                      <span className="font-medium">{c.name}</span>
                      <span className="text-muted-foreground ml-auto">{c.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="7700 900 123"
              className={`input-field flex-1 ${errors.phone ? 'error' : ''}`}
              {...register('phone', {
                required: t.auth.phoneRequiredReg,
                pattern: { value: /^[0-9\s]{7,15}$/, message: t.auth.phoneInvalidReg },
              })}
            />
          </div>
          {errors.phone && (
            <p className="text-xs text-negative mt-1">{errors.phone.message}</p>
          )}
        </div>

        <div className="flex items-start gap-3 pt-1">
          <input
            type="checkbox"
            id="agreeTerms"
            className="mt-0.5 w-4 h-4 accent-primary"
            {...register('agreeTerms', { required: t.auth.termsRequired })}
          />
          <label htmlFor="agreeTerms" className="text-xs text-muted-foreground leading-relaxed">
            {t.auth.agreeTerms}{' '}
            <a href="#" className="text-primary hover:underline">{t.auth.termsOfService}</a>
            {' '}{t.auth.and}{' '}
            <a href="#" className="text-primary hover:underline">{t.auth.privacyPolicy}</a>.
            {' '}{t.auth.fscsProtected}
          </label>
        </div>
        {errors.agreeTerms && (
          <p className="text-xs text-negative">{errors.agreeTerms.message}</p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t.auth.creatingAccount}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <UserPlus size={16} />
              {t.auth.createAccount}
            </span>
          )}
        </button>
        {rateLimitError && (
          <p className="text-xs text-negative text-center bg-negative/8 border border-negative/20 rounded-lg px-3 py-2">
            {rateLimitError}
          </p>
        )}
      </form>
    </div>
  );
}